import { styled, useTheme } from '@mui/material/styles';
import React from 'react';

import { SnackbarActionTypes, useSnackbarDispatch, useSnackbarState } from 'contexts/snackbar/SnackbarContext';

import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';

import { Alert, AlertTitle, Box, Button } from '@mui/material';
import Dialog from 'components/dialog';
import { OctagonX } from 'lucide-react';
import { useIntl } from 'react-intl';
import { getErrorMessage } from 'utils/api-service';

const Pre = styled('pre')(({ theme }) => ({
  whiteSpace: 'break-spaces',
  wordBreak: 'normal',
}));

const SnackbarButton = styled(Button)(({ theme }) => ({
  color: theme.palette.darkBlue.main,
  borderColor: theme.palette.darkBlue.main,
  '&:hover': {
    color: theme.palette.darkBlue.main,
    borderColor: theme.palette.darkBlue.main,
  },
}));

const Root = styled(Alert)(({ theme }) => ({
  paddingLeft: '49px',
  backgroundColor: theme.palette.red.o50,
  ['& .MuiAlert-message']: {
    width: '100%',
  },
  ['& .MuiAlert-action']: {
    button: {
      color: theme.palette.darkBlue.main,
    },
    marginLeft: 0,
    color: theme.palette.darkBlue.main,
  },
}));

type Props = {};

const ErrorSnackbar: React.FC<Props> = () => {
  const { msg, isError, error } = useSnackbarState();
  const { errorMsg } = getErrorMessage(error);
  const theme = useTheme();
  const dispatch = useSnackbarDispatch();
  const intl = useIntl();
  const hasFieldErrors = errorMsg?.length > 0;

  const handleClose = () => {
    dispatch({ type: SnackbarActionTypes.CLEAR_SNACKBAR });
  };

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      onClose={handleClose}
      autoHideDuration={10000}
      open={isError}
      TransitionComponent={Slide}
      key={Slide.name}>
      <Root elevation={6} variant="filled" onClose={handleClose} icon={false}>
        <Box
          sx={{
            padding: theme.spacing(4),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
          <OctagonX color={theme.palette.darkBlue.main} size={32} />
          <AlertTitle
            sx={{
              padding: theme.spacing(3, 0),
              fontSize: 20,
              color: theme.palette.darkBlue.main,
            }}>
            {msg || intl.formatMessage({ id: 'snackbar.errors.default' })}
          </AlertTitle>
          {hasFieldErrors && (
            <Dialog
              title={'Error Message'}
              onClose={handleClose}
              sx={{
                zIndex: 2000,
                '& .MuiDialogContent-root': {
                  maxHeight: '50vh',
                },
              }}
              button={
                <SnackbarButton
                  variant="outlined"
                  sx={{
                    margin: theme.spacing(3, 0),
                  }}>
                  Show Error
                </SnackbarButton>
              }>
              <Box display="flex" flexDirection="column">
                <ul
                  style={{
                    paddingLeft: 0,
                    marginTop: 0,
                    listStyle: "none",
                  }}
                >
                  {errorMsg?.map((e, i) => (
                    <li key={i}>{e.message}</li>
                  ))}
                </ul>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    alignSelf: "flex-end",
                    mt: 1,
                    textTransform: "none",
                  }}
                  onClick={() =>
                    navigator.clipboard.writeText(JSON.stringify(error?.response?.data, null, 2))
                  }
                >
                  Copy Error
                </Button>
              </Box>
            </Dialog>
          )}
          {error && !hasFieldErrors && (
            <Dialog
              title={'Error Message'}
              onClose={handleClose}
              sx={{
                zIndex: 2000,
                '& .MuiDialogContent-root': {
                  maxHeight: '50vh',
                },
              }}
              button={
                <SnackbarButton
                  variant="outlined"
                  sx={{
                    margin: theme.spacing(3, 0),
                  }}>
                  Show Error
                </SnackbarButton>
              }>
              <Pre>{JSON.stringify(error.response?.data, null, 2)}</Pre>
            </Dialog>
          )}
          <SnackbarButton variant="outlined" fullWidth onClick={handleClose}>
            {intl.formatMessage({ id: 'general.ok' })}
          </SnackbarButton>
        </Box>
      </Root>
    </Snackbar>
  );
};

export default ErrorSnackbar;
